// Scenario 6: Not Booked - No Preference  
// Shows island-wide recommendations with primary/secondary choices
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// Removed unused firebase callable imports
import { islandScopedDocumentFilter, sanityFetch } from '../../lib/sanity';
import { useRegionsData } from '../../hooks/useRegionsData';
import RegionsMapLite from '../maps/RegionsMapLite';
import AreaProfileHero from './AreaProfileHero';
import AreaProfileSidebar from './AreaProfileSidebar';
import NearbyAreas from './NearbyAreas';
import HomebaseActionPanel from './HomebaseActionPanel';
import {
  getHomebaseAreaSlug,
  getHomebaseIslandKey,
  isHomebaseItineraryReady,
  MULTI_ISLAND_PLANNER_COMING_SOON_PATH,
} from '../../utils/homebaseGeography';

const IslandWideScenario = ({ data, interviewId, planningState = {} }) => {
  const navigate = useNavigate();
  
  // Debug logging to track data changes
  React.useEffect(() => {
    console.log('[IslandWideScenario] Data changed:', {
      hasData: !!data,
      scenario: data?.scenario,
      primaryArea: data?.summary?.primaryRecommendation?.area,
      dataKeys: data ? Object.keys(data) : null,
      timestamp: new Date().toISOString()
    });
  }, [data]);
  const [displayedArea, setDisplayedArea] = useState(null); // Currently shown area
  const [displayedRegion, setDisplayedRegion] = useState(null); // Currently shown region
  const [areaData, setAreaData] = useState(null); // Full area data from Sanity
  const [isShowingRecommendation, setIsShowingRecommendation] = useState(true); // vs user selection
  const [showingAlternative, setShowingAlternative] = useState(false); // primary vs alternative

  const { ready, loading, error, retry } = planningState;
  const ctaPlanningState = useMemo(() => ({
    ready,
    loading,
    error,
    retry
  }), [ready, loading, error, retry]);
  
  // Get master areas data for validation
  const { areasData: allAreas } = useRegionsData();
  const islandKey = getHomebaseIslandKey(data);
  const itineraryReady = isHomebaseItineraryReady(islandKey);
  
  // Extract data from backend response
  const primaryRecommendation = useMemo(() => ({
    area: data?.summary?.primaryRecommendation?.area,
    region: data?.summary?.primaryRecommendation?.region,
    summary: data?.summary?.primaryRecommendation
  }), [data?.summary?.primaryRecommendation]);
  
  const alternativeRecommendation = useMemo(() => ({
    area: data?.summary?.secondaryRecommendation?.area,
    region: data?.summary?.secondaryRecommendation?.region,
    summary: data?.summary?.secondaryRecommendation
  }), [data?.summary?.secondaryRecommendation]);

  // Get current recommendation based on toggle state
  const getCurrentRecommendation = useCallback(() => {
    return showingAlternative ? alternativeRecommendation : primaryRecommendation;
  }, [showingAlternative, alternativeRecommendation, primaryRecommendation]);

  // Initialize displayed area/region with primary recommendation
  useEffect(() => {
    const current = getCurrentRecommendation();
    if (current.area && !displayedArea) {
      setDisplayedArea(current.area);
      setDisplayedRegion(current.region);
    }
  }, [displayedArea, showingAlternative, getCurrentRecommendation]);

  // Update displayed area/region when toggling recommendations
  useEffect(() => {
    const current = getCurrentRecommendation();
    if (isShowingRecommendation && current.area) {
      setDisplayedArea(current.area);
      setDisplayedRegion(current.region);
    }
  }, [showingAlternative, isShowingRecommendation, getCurrentRecommendation]);

  // Fetch area data from Sanity when displayed area changes
  useEffect(() => {
    const fetchAreaData = async () => {
      if (!displayedArea) return;
      
      try {
        const explicitSlug = displayedArea === data?.area ? data?.areaSlug : null;
        const areaSlug = getHomebaseAreaSlug(displayedArea, islandKey, explicitSlug);
        
        const query = `*[_type == "area" && ${islandScopedDocumentFilter()} && slug.current == $slug][0]{
          _id,
          title,
          oneLiner,
          heroImage{asset->{url}, alt},
          slug,
          fit,
          tradeoffs,
          walkability,
          beachProfile,
          diningDensity,
          logisticsNotes,
          signatureExperiences[]->{
            _id,
            title,
            slug,
            heroImage{asset->{url}, alt},
            oneLiner
          },
          region->{
            _id,
            title,
            slug
          }
        }`;
        
        const result = await sanityFetch(query, { slug: areaSlug });
        
        if (result) {
          setAreaData(result);
          // Update displayed region from fetched data if needed
          if (result.region?.title) {
            setDisplayedRegion(result.region.title);
          }
        } else {
          // Fallback to basic data
          setAreaData({
            title: displayedArea,
            oneLiner: `Beautiful ${displayedArea} offers stunning scenery and local charm in ${displayedRegion}.`,
            heroImage: { asset: { url: '/api/placeholder/800/400' }, alt: `${displayedArea} landscape` }
          });
        }
      } catch (error) {
        console.error('[IslandWideScenario] Error fetching area data:', error);
        // Fallback to basic data
        setAreaData({
          title: displayedArea,
          oneLiner: `Beautiful ${displayedArea} offers stunning scenery and local charm in ${displayedRegion}.`,
          heroImage: { asset: { url: '/api/placeholder/800/400' }, alt: `${displayedArea} landscape` }
        });
      }
    };

    fetchAreaData();
  }, [displayedArea, displayedRegion, islandKey, data?.area, data?.areaSlug]);

  // Early return check after hooks
  if (!data || !primaryRecommendation.area) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white/70">No homebase data available for this scenario.</p>
      </div>
    );
  }

  const handleEditInterview = () => {
    navigate(`/interview/review/${interviewId}`);
  };

  const handleGenerateItinerary = () => {
    if (!itineraryReady) {
      navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH);
      return;
    }

    // Prefer the currently displayed selection; fall back to recommendation
    const areaToConfirm = displayedArea || primaryRecommendation.area;
    const regionToConfirm = displayedRegion || primaryRecommendation.region;

    if (!areaToConfirm || !regionToConfirm) {
      console.warn('[IslandWideScenario] Missing area or region on generate click:', {
        displayedArea,
        displayedRegion,
        fallbackArea: primaryRecommendation.area,
        fallbackRegion: primaryRecommendation.region
      });
    }

    // Navigate to preview and let the preview page perform homebaseConfirm first
    const q = new URLSearchParams({
      confirmArea: areaToConfirm || '',
      confirmRegion: regionToConfirm || '',
      scenario: 'not-booked-no-pref'
    }).toString();
    navigate(`/itinerary/${interviewId}?${q}`);
  };

  // Normalize area names for comparison (handles Hawaiian diacritics)
  const normalizeAreaName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/[ʻ'`]/g, '')  // Remove ʻokinas and apostrophes
      .replace(/[āáàâä]/g, 'a')
      .replace(/[ēéèêë]/g, 'e')
      .replace(/[īíìîï]/g, 'i')
      .replace(/[ōóòôö]/g, 'o')
      .replace(/[ūúùûü]/g, 'u')
      .trim();
  };

  const handleAreaChange = (areaName) => {
    setDisplayedArea(areaName);
    
    // Use normalized comparison to handle Hawaiian diacritics
    // Check if the selected area matches either our primary or alternative recommendation
    const isPrimaryRecommendation = normalizeAreaName(areaName) === normalizeAreaName(primaryRecommendation.area);
    const isAlternativeRecommendation = normalizeAreaName(areaName) === normalizeAreaName(alternativeRecommendation.area);
    const isAnyRecommendation = isPrimaryRecommendation || isAlternativeRecommendation;
    
    setIsShowingRecommendation(isAnyRecommendation);
    
    // Update the toggle state to match which recommendation they selected
    if (isPrimaryRecommendation) {
      setShowingAlternative(false);
    } else if (isAlternativeRecommendation) {
      setShowingAlternative(true);
    }
  };

  const handleMapAreaClick = (type, areaName) => {
    if (type === 'area' && allAreas) {
      // Validate area against master list
      const area = allAreas.find(a => 
        a.title === areaName ||
        a.title.toLowerCase() === areaName.toLowerCase() ||
        a.slug.current === areaName ||
        a.slug.current === areaName.toLowerCase()
      );
      
      if (area) {
        // Allow selection of any area island-wide
        handleAreaChange(area.title);
        setDisplayedRegion(area.region?.title);
      }
    }
  };

  const handleToggleRecommendation = () => {
    if (!isShowingRecommendation) {
      // If user is off-menu, always go back to first choice
      setShowingAlternative(false);
      setDisplayedArea(primaryRecommendation.area);
      setDisplayedRegion(primaryRecommendation.region);
    } else {
      // If user is viewing a recommendation, toggle between first and second
      setShowingAlternative(!showingAlternative);
      const newRecommendation = !showingAlternative ? alternativeRecommendation : primaryRecommendation;
      setDisplayedArea(newRecommendation.area);
      setDisplayedRegion(newRecommendation.region);
    }
    setIsShowingRecommendation(true); // Always show recommendation after clicking
  };


  const currentRecommendation = getCurrentRecommendation();

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 pb-8">
        
        {/* Primary CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
          <button
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={ctaPlanningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>

        {/* Situation & Regional Overview - Always visible */}
        <div className="glass-card rounded-lg p-6 mb-8">
          {/* The Situation */}
          {data?.summary?.lodgingInsights && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-white">Let's Find the Perfect Home Base for Your Trip</h3>
              <p className="text-white/90 leading-relaxed">
                {data.summary.lodgingInsights}
              </p>
            </div>
          )}

          {/* Regional Considerations */}
          {data?.summary?.regionComparison && data.summary.regionComparison.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-white">Regional Considerations</h3>
              <ul className="space-y-3">
                {data.summary.regionComparison.map((comparison, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-400 mr-3 mt-1">•</span>
                    <span className="text-white/90">{comparison}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Recommendation Header */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2 text-white">
            {isShowingRecommendation ? 
              `We recommend ${areaData?.title || displayedArea} in ${displayedRegion} as our ${showingAlternative ? 'second' : 'first'} choice.` :
              `You've selected ${areaData?.title || displayedArea} in ${displayedRegion}.`
            }
          </h2>
        </div>

        {/* Area-Specific Recommendation - Only show if displaying a recommendation */}
        {isShowingRecommendation && currentRecommendation.summary && (
          <div className="glass-card rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 text-white">
              Why {areaData?.title || displayedArea} in {displayedRegion}:
            </h3>
            
            {/* Area-Specific Reasons */}
            {currentRecommendation.summary?.explanation && currentRecommendation.summary.explanation.length > 0 && (
              <ul className="space-y-3">
                {currentRecommendation.summary.explanation.map((reason, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-400 mr-3 mt-1">•</span>
                    <span className="text-white/90">{reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Action Buttons - Toggle and Primary CTA */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-6">
          <button
            onClick={handleToggleRecommendation}
            className="btn-secondary px-8 py-4 text-lg md:order-2"
          >
            {isShowingRecommendation && showingAlternative ? 'See our first choice' :
              !isShowingRecommendation ? 'See our first choice' : 'See our second choice'}
          </button>
          <div className="md:order-2">
            <HomebaseActionPanel
              planningState={ctaPlanningState}
              onGenerate={handleGenerateItinerary}
              ctaLabel="Generate Day 1 Itinerary"
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>

        {/* Area Profile */}
        {areaData ? (
          <div className="mb-8">
            {/* Hero Section */}
            <AreaProfileHero areaData={areaData} />

            {/* Overview Section with Map */}
            <div className="grid md:grid-cols-2 gap-12 mb-12">
              {/* Left Sidebar - Area Details */}
              <AreaProfileSidebar areaData={areaData} />

              {/* Interactive Map */}
              <div>
                <div className="h-[400px] rounded-lg overflow-hidden border border-gray-600">
                  {allAreas ? (
                    <RegionsMapLite
                      islandKey={islandKey}
                      selectable={true}
                      areasSelectable={true}
                      selectedRegion={displayedRegion}
                      selectedArea={displayedArea}
                      onSelect={handleMapAreaClick}
                      focusedAreaMode={true}
                      className="w-full h-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                      <p>Loading map...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-12 flex items-center justify-center h-48">
            <p className="text-white/70">Loading area profile...</p>
          </div>
        )}

        {/* Action Buttons - Toggle and Primary CTA */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-6">
          <button
            onClick={handleToggleRecommendation}
            className="btn-secondary px-8 py-4 text-lg md:order-2"
          >
            {isShowingRecommendation && showingAlternative ? 'See our first choice' :
              !isShowingRecommendation ? 'See our first choice' : 'See our second choice'}
          </button>
          <div className="md:order-2">
            <HomebaseActionPanel
              planningState={ctaPlanningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>

        {/* Nearby Areas */}
        <NearbyAreas 
          currentAreaTitle={areaData?.title}
          regionTitle={displayedRegion}
          regionSlug={areaData?.region?.slug?.current}
          onAreaSelect={handleAreaChange}
        />

        {/* Bottom CTAs */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <button 
            onClick={handleEditInterview}
            className="btn-secondary w-full md:w-72 px-8 py-4 text-lg"
          >
            Edit Interview
          </button>
          <div className="w-full md:w-72">
            <HomebaseActionPanel
              planningState={ctaPlanningState}
              onGenerate={handleGenerateItinerary}
              itineraryReady={itineraryReady}
              onUnavailable={() => navigate(MULTI_ISLAND_PLANNER_COMING_SOON_PATH)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IslandWideScenario;
